import { useCallback, useEffect, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import {
  ALL_DISCLOSURE_EVENT_TYPES,
  ALL_DISCLOSURE_REVIEW_STATUSES,
  DISCLOSURE_PAGE_SIZES,
  type DisclosureEventType,
  type DisclosureFilters,
  type DisclosureReviewStatus,
} from './disclosures.types';

// 권위: ARCH_DASHBOARD §4.6 탭 6-A 필터 바.
// - 기간 (from/to date picker, 기본 최근 30일)
// - 종목 필터 (6자리 코드)
// - 검색어 (제목/본문, debounce 400ms)
// - 이벤트 유형 (select)
// - review_status (다중 체크박스 드롭다운)
// - page_size (50/100/200)
// - 초기화 버튼
//
// 스타일: SystemLog 의 syslog-filter 토큰을 재사용 (원장 톤 유지).

interface Props {
  filters: DisclosureFilters;
  onChange: (next: DisclosureFilters) => void;
  onReset: () => void;
}

export function DisclosuresFilterBar({ filters, onChange, onReset }: Props): JSX.Element {
  // q, symbol 은 debounce 400ms (타이핑 중 매 keystroke 마다 요청 방지).
  const [qLocal, setQLocal] = useState(filters.q);
  const [symbolLocal, setSymbolLocal] = useState(filters.symbol);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    setQLocal(filters.q);
  }, [filters.q]);
  useEffect(() => {
    setSymbolLocal(filters.symbol);
  }, [filters.symbol]);

  const scheduleCommit = useCallback(
    (next: DisclosureFilters) => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        onChange(next);
      }, 400);
    },
    [onChange]
  );
  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    },
    []
  );

  // ── review_status 다중 선택 드롭다운 ───────────────
  const [statusOpen, setStatusOpen] = useState(false);
  const statusRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!statusOpen) return;
    function onDoc(e: MouseEvent): void {
      if (!statusRef.current) return;
      if (!statusRef.current.contains(e.target as Node)) setStatusOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [statusOpen]);

  const toggleStatus = useCallback(
    (s: DisclosureReviewStatus) => {
      const has = filters.reviewStatus.includes(s);
      const next = has
        ? filters.reviewStatus.filter((x) => x !== s)
        : [...filters.reviewStatus, s];
      onChange({ ...filters, reviewStatus: next, page: 0 });
    },
    [filters, onChange]
  );

  return (
    <div className="syslog-filter">
      <div className="syslog-filter__group">
        <span className="syslog-filter__label">From</span>
        <input
          className="syslog-input"
          type="date"
          value={filters.from}
          onChange={(e) => onChange({ ...filters, from: e.target.value, page: 0 })}
        />
      </div>

      <div className="syslog-filter__group">
        <span className="syslog-filter__label">To</span>
        <input
          className="syslog-input"
          type="date"
          value={filters.to}
          onChange={(e) => onChange({ ...filters, to: e.target.value, page: 0 })}
        />
      </div>

      <div className="syslog-filter__group">
        <span className="syslog-filter__label">종목코드</span>
        <input
          className="syslog-input"
          type="text"
          inputMode="numeric"
          maxLength={6}
          placeholder="예: 005930"
          value={symbolLocal}
          onChange={(e) => {
            // 6자리 숫자만 허용하지만, 백엔드 검증도 있으므로 여기서는 가볍게.
            const v = e.target.value.replace(/\s+/g, '');
            setSymbolLocal(v);
            scheduleCommit({ ...filters, symbol: v, page: 0 });
          }}
        />
      </div>

      <div className="syslog-filter__group">
        <span className="syslog-filter__label">이벤트 유형</span>
        <select
          className="syslog-input"
          value={filters.eventType}
          onChange={(e) =>
            onChange({
              ...filters,
              eventType: (e.target.value as DisclosureEventType | '') ?? '',
              page: 0,
            })
          }
        >
          <option value="">전체</option>
          {ALL_DISCLOSURE_EVENT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div className="syslog-filter__group" ref={statusRef}>
        <span className="syslog-filter__label">Review status</span>
        <button
          type="button"
          className="syslog-input"
          onClick={() => setStatusOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={statusOpen}
        >
          {filters.reviewStatus.length === 0 ? '전체' : filters.reviewStatus.join(', ')}
        </button>
        {statusOpen && (
          <div className="syslog-filter__dropdown" role="listbox">
            {ALL_DISCLOSURE_REVIEW_STATUSES.map((s) => {
              const active = filters.reviewStatus.includes(s);
              return (
                <label key={s} className="syslog-filter__dropdown-row">
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() => toggleStatus(s)}
                  />
                  <span>{s}</span>
                </label>
              );
            })}
            {filters.reviewStatus.length > 0 && (
              <button
                type="button"
                className="syslog-btn syslog-btn--ghost"
                onClick={() => onChange({ ...filters, reviewStatus: [], page: 0 })}
              >
                선택 해제
              </button>
            )}
          </div>
        )}
      </div>

      <div className="syslog-filter__group syslog-filter__group--grow">
        <span className="syslog-filter__label">검색 (제목/본문)</span>
        <input
          className="syslog-input"
          type="text"
          placeholder="키워드 입력"
          value={qLocal}
          onChange={(e) => {
            setQLocal(e.target.value);
            scheduleCommit({ ...filters, q: e.target.value, page: 0 });
          }}
        />
      </div>

      <div className="syslog-filter__group">
        <span className="syslog-filter__label">Page size</span>
        <select
          className="syslog-input"
          value={filters.pageSize}
          onChange={(e) =>
            onChange({ ...filters, pageSize: parseInt(e.target.value, 10), page: 0 })
          }
        >
          {DISCLOSURE_PAGE_SIZES.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>

      <div className="syslog-filter__group">
        <span className="syslog-filter__label">실패 마커</span>
        <label className="syslog-input" style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={filters.includeFailed}
            onChange={(e) => onChange({ ...filters, includeFailed: e.target.checked, page: 0 })}
          />
          <span>포함</span>
        </label>
      </div>

      <div className="syslog-filter__group">
        <span className="syslog-filter__label">&nbsp;</span>
        <button type="button" className="syslog-btn syslog-btn--ghost" onClick={onReset}>
          <RotateCcw size={14} strokeWidth={1.75} />
          초기화
        </button>
      </div>
    </div>
  );
}
